import { Box, Card, Grid, Icon, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";
import { wrapper } from "utils/wrapper";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import Swal from "sweetalert2";
import DOMPurify from 'dompurify';

import api from "services/api/translations"; // Note: Adjust import path to your new translations API file
import RevisedTable from "components/Tables/RevisedTable";

function TranslationsView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    api.loadToken(useAuth0());

    const queryClient = useQueryClient();

    const canTranslationsAdd = userPermissions.indexOf('translations:add') >= 0;
    const canTranslationsEdit = userPermissions.indexOf('translations:edit') >= 0;
    const canTranslationsDelete = userPermissions.indexOf('translations:delete') >= 0;

    // --- STATE ---
    const [selectedType, setSelectedType] = useState(null); // Holds the selected translationType string
    const [searchQuery, setSearchQuery] = useState("");

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // Null if adding new, object if editing
    const [formData, setFormData] = useState({ input: "", output: "" });

    // --- QUERIES ---
    const translationTypes = useQuery({
        queryKey: ['translationTypes', selectedOrg],
        queryFn: () => api.getTranslationTypes(),
        staleTime: 5 * 60 * 1000
    });

    const translations = useQuery({
        queryKey: ['translations', selectedOrg],
        queryFn: () => api.getTranslations(),
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (translationTypes?.data && translationTypes?.data.length > 0 && !selectedType) {
            setSelectedType(translationTypes.data.filter(row => row.type != 'blacklist')[0].type);
        }
    }, [translationTypes.data, selectedType]);

    // --- MUTATIONS ---
    const saveMutation = useMutation({
        mutationFn: (data) => editingItem
            ? api.updateTranslation(editingItem._id, data)
            : api.createTranslation(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['translations', selectedOrg] });
            closeModal();
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Saved!</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        },
        onError: (error) => {
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${error.message || "Failed to save translation. Ensure the input is unique."}</div>`, "error");
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => api.deleteTranslation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['translations', selectedOrg] });
            Swal.fire('<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Deleted!</div>', '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">The translation has been removed.</div>', 'success');
        }
    });

    // --- HANDLERS ---
    const handleAddClick = () => {
        if (!selectedType) return Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Select a Type</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Please select a translation type first.</div>`, "info");
        setEditingItem(null);
        setFormData({ input: "", output: "", translationType: selectedType });
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        setEditingItem(item);
        setFormData({ input: item.input, output: item.output, translationType: item.translationType });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (item) => {

        let campaign_name = DOMPurify.sanitize(item.input, { ALLOWED_TAGS: [] });

        Swal.fire({
            title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Are you sure?</div>',
            html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">You are about to delete the translation for "${campaign_name}".</div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#cb0c9f',
            cancelButtonColor: '#8392ab',
            confirmButtonText: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Yes, delete it!</div>'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(item._id);
            }
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ input: "", output: "" });
    };

    // --- FILTERING ---
    const typeOptions = translationTypes.data?.filter(row => row.type != 'blacklist').map(t => ({ value: t.type, label: t.label, input_type: t.input_type })) || [];
    const activeTypeObj = typeOptions.find(opt => opt.value === selectedType) || null;

    const filteredTranslations = translations.data?.filter(t => {
        const matchesType = t.translationType === selectedType;
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = t.input.toLowerCase().includes(searchLower) || t.output.toLowerCase().includes(searchLower);
        return matchesType && matchesSearch;
    }) || [];

    // --- SAVE HANDLING ---
    const handleSave = () => {
        const input = !formData.input.trim();
        const output = (activeTypeObj.input_type == "form" && !formData.input.trim());

        if (input || output) {
            return Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Required</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Both Input and Output are required.</div>`, "warning");
        }
        saveMutation.mutate(formData);
    };

    // --- UI COMPONENTS ---
    const mainContent = (
        <SoftBox mx={1} display="flex" flexDirection="column" borderRadius="16px" color={"white"} width="100%" height="100%"
            sx={({ functions: { rgba } }) => ({
                backgroundColor: rgba("#eeeeee", 1),
                overflow: "hidden", // Clips any horizontal bleed
                minWidth: 0,        // Allows the box to shrink to fit the screen
                width: '100%'
            })}
        >

            {/* Header Controls */}
            <SoftBox py={1} px={2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xxs={12} md={canTranslationsAdd ? 4 : 6}>
                        <SoftBox mb={0.5} pl={0.5}>
                            <SoftTypography variant="caption" fontWeight="bold">Translation Category</SoftTypography>
                        </SoftBox>
                        <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%' }}>
                            <SoftSelect
                                size="medium"
                                value={activeTypeObj}
                                options={typeOptions}
                                onChange={(selected) => setSelectedType(selected?.value)}
                                placeholder="Select a category..."
                                isDisabled={translationTypes.isLoading}
                            />
                        </SoftBox>
                    </Grid>
                    <Grid item xxs={12} md={canTranslationsAdd ? 5 : 6}>
                        <SoftBox mb={0.5} pl={0.5}>
                            <SoftTypography variant="caption" fontWeight="bold">Search Translations</SoftTypography>
                        </SoftBox>
                        <SoftBox sx={{ background: '#fff', borderRadius: '0.5rem' }}>
                            <SoftInput
                                placeholder="Search input or output..."
                                icon={{ component: "search", direction: "left" }}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </SoftBox>
                    </Grid>
                    {canTranslationsAdd && (
                        <Grid item xxs={12} md={3} display="flex" justifyContent="flex-end" alignItems="flex-end">
                            <SoftBox mt={{ xxs: 0, md: 3 }} width="100%">
                                <SoftButton variant="gradient" color="dark" fullWidth onClick={handleAddClick} disabled={!selectedType}>
                                    <Icon sx={{ mr: 1 }}>add</Icon> Add New
                                </SoftButton>
                            </SoftBox>
                        </Grid>
                    )}
                </Grid>
            </SoftBox>

            {/* Translation Table Area */}
            <SoftBox height="auto" sx={{ overflowY: 'auto' }} p={2} flex={1}>
                {(translations.isLoading || translationTypes.isLoading) ? (
                    <SoftBox display="flex" justifyContent="center" alignItems="center" p={5}>
                        <CircularProgress color="inherit" />
                    </SoftBox>
                ) : (selectedType == null || filteredTranslations.length === 0) ? (
                    <>
                        {(selectedType == null) && (
                            <SoftBox display="flex" justifyContent="center" alignItems="center" p={5} flexDirection="column">
                                <Icon fontSize="large" sx={{ color: '#aaa', mb: 1 }}>translate</Icon>
                                <SoftTypography variant="button" color="text" fontWeight="medium" textAlign="center">
                                    A translation category has not been selected.
                                </SoftTypography>
                            </SoftBox>
                        )}
                        {(selectedType != null && filteredTranslations.length == 0) && (
                            <SoftBox display="flex" justifyContent="center" alignItems="center" p={5} flexDirection="column">
                                <Icon fontSize="large" sx={{ color: '#aaa', mb: 1 }}>translate</Icon>
                                <SoftTypography variant="button" color="text" fontWeight="medium" textAlign="center">
                                    No translations found for this category.
                                </SoftTypography>
                            </SoftBox>
                        )}
                    </>
                ) : (
                    <Card sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
                        <SoftBox p={0} sx={{ overflowX: "auto" }}>
                            <Box component="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
                                <Box component="thead" sx={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                                    <Box component="tr">
                                        <Box component="th" py={2} px={{ xxs: 1, xs: 2 }} textAlign="left"><SoftTypography variant="caption" fontWeight="bold" color="secondary">INPUT TEXT</SoftTypography></Box>
                                        {activeTypeObj?.input_type == "form" && (
                                            <Box component="th" py={2} px={{ xxs: 1, xs: 2 }} textAlign="left"><SoftTypography variant="caption" fontWeight="bold" color="secondary">OUTPUT TRANSLATION</SoftTypography></Box>
                                        )}
                                        <Box component="th" py={2} px={{ xxs: 1, xs: 2 }} textAlign="right">
                                            {(canTranslationsEdit || canTranslationsDelete) && (
                                                <SoftTypography variant="caption" fontWeight="bold" color="secondary">ACTIONS</SoftTypography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {filteredTranslations.map((item) => (
                                        <Box component="tr" key={item._id} sx={{ borderBottom: '1px solid #e0e0e0', '&:hover': { backgroundColor: '#f8f9fa' } }}>
                                            <Box component="td" p={2}>
                                                <SoftTypography variant="button" fontWeight="medium" color="text">{item.input}</SoftTypography>
                                            </Box>
                                            {activeTypeObj?.input_type == "form" && (
                                                <Box component="td" p={2}>
                                                    <SoftTypography variant="button" fontWeight="medium" color="dark">{item.output}</SoftTypography>
                                                </Box>
                                            )}
                                            <Box component="td" p={2} textAlign="right">
                                                <Box display='flex' flexDirection={{ xxs: 'column', xs: 'row' }} justifyContent={'flex-end'} gap={deviceLayout == 'mobile' ? 0.5 : 1}>
                                                    {canTranslationsEdit && (
                                                        <IconButton size="small" color="secondary" onClick={() => handleEditClick(item)}>
                                                            <Icon>edit</Icon>
                                                        </IconButton>
                                                    )}
                                                    {canTranslationsDelete && (
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(item)}>
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </SoftBox>
                    </Card>
                )}
            </SoftBox>
        </SoftBox>
    );

    const data_table_data = {
        columns: [
            {
                Header: 'Input Text',
                accessor: 'input',
            },
            {
                Header: 'Output Translation',
                accessor: 'output',
                weight: 'medium',
            },
            {
                Header: 'Actions',
                accessor: 'translationType',
                align: 'right',
                width: '50px',
                Cell: ({ cell }) => (
                    <Box display='flex' flexDirection={'row'} gap={deviceLayout == 'mobile' ? 0.5 : 1}>
                        {canTranslationsEdit && (
                            <IconButton size="small" color="secondary" onClick={() => handleEditClick(cell.row.original)}>
                                <Icon>edit</Icon>
                            </IconButton>
                        )}
                        {canTranslationsDelete && (
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(cell.row.original)}>
                                <Icon>delete</Icon>
                            </IconButton>
                        )}
                    </Box>
                )
            }
        ],
        rows: filteredTranslations
    };

    const entriesPerPage = { defaultValue: 15, entries: [15, 30, 45, 60] };

    return (
        <>
            {wrapper((
                <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
                    sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                >
                    <SoftBox my={1} display="flex" sx={{ borderRadius: '16px', height: `calc(100% - 16px)` }}>
                        <SoftBox mx={1} display="flex" flexDirection="column" borderRadius="16px" color={"white"} width="100%" height="100%"
                            sx={({ functions: { rgba } }) => ({
                                backgroundColor: rgba("#eeeeee", 1),
                                overflow: "hidden", // Clips any horizontal bleed
                                minWidth: 0,        // Allows the box to shrink to fit the screen
                                width: '100%'
                            })}
                        >
                            <RevisedTable table={data_table_data} entriesPerPage={entriesPerPage} canAdd={{ state: canTranslationsAdd, func: handleAddClick }} canSearch typeOptions={typeOptions} types={{ state: typeOptions.find(opt => opt.value === selectedType) || null, func: setSelectedType }} />
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            ), controller)}

            {/* Add / Edit Dialog */}
            <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '1rem' } }}>
                <DialogTitle>
                    <SoftTypography variant="h6" fontWeight="bold">
                        {editingItem ? "Edit Translation" : "Add New Translation"}
                    </SoftTypography>
                </DialogTitle>
                <DialogContent>
                    <SoftBox pt={1} pb={2}>
                        <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Original Input</SoftTypography>
                        <SoftInput
                            placeholder="e.g., Central Station"
                            value={formData.input}
                            onChange={(e) => setFormData({ ...formData, input: e.target.value })}
                            disabled={saveMutation.isPending}
                        />
                    </SoftBox>
                    {activeTypeObj?.input_type == "form" && (
                        <SoftBox pb={1}>
                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Translated Output</SoftTypography>
                            <SoftInput
                                placeholder="e.g., Central"
                                value={formData.output}
                                onChange={(e) => setFormData({ ...formData, output: e.target.value })}
                                disabled={saveMutation.isPending}
                            />
                        </SoftBox>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <SoftButton onClick={closeModal} variant="text" color="secondary" disabled={saveMutation.isPending}>
                        Cancel
                    </SoftButton>
                    <SoftButton onClick={handleSave} variant="gradient" color="dark" disabled={saveMutation.isPending}>
                        {saveMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </SoftButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

TranslationsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
}

export default TranslationsView;